import { useState } from 'react';
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import BarChartIcon from '@mui/icons-material/BarChart';
import TableChartIcon from '@mui/icons-material/TableChart';
import DownloadIcon from '@mui/icons-material/Download';
import { useTranslation } from 'react-i18next';

type ViewMode = 'chart' | 'table';

interface ViewModeToggleProps {
  value: ViewMode;
  onChange: (mode: ViewMode) => void;
  onDownload: () => void;
  size?: 'small' | 'medium';
  sx?: SxProps<Theme>;
}

export const ViewModeToggle = ({
  value,
  onChange,
  onDownload,
  size = 'small',
  sx,
}: ViewModeToggleProps) => {
  const { t } = useTranslation();
  const [openTooltip, setOpenTooltip] = useState<
    'chart' | 'table' | 'download' | null
  >(null);

  const tooltipProps = (name: 'chart' | 'table' | 'download') => ({
    onFocus: () => setOpenTooltip(name),
    onBlur: () =>
      setOpenTooltip((prev) => (prev === name ? null : prev)),
    onMouseEnter: () => setOpenTooltip(name),
    onMouseLeave: () =>
      setOpenTooltip((prev) => (prev === name ? null : prev)),
  });

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: 'chart' | 'table' | 'download' | null
  ) => {
    if (newMode === 'download') {
      onDownload();
    } else if (newMode) {
      onChange(newMode);
    }
  };

  return (
    <ToggleButtonGroup
      value={value}
      exclusive
      onChange={handleChange}
      size={size}
      aria-label={String(
        t('statistics.viewMode.ariaLabel', 'View mode options')
      )}
      sx={sx}
    >
      <ToggleButton
        value="chart"
        aria-label={String(t('statistics.view.chart', 'Chart view'))}
        size={size}
        {...tooltipProps('chart')}
      >
        <Tooltip
          title={t('statistics.view.chart', 'Chart view')}
          open={openTooltip === 'chart'}
          disableFocusListener
          disableHoverListener
        >
          <BarChartIcon sx={{ fontSize: 18 }} />
        </Tooltip>
      </ToggleButton>
      <ToggleButton
        value="table"
        aria-label={String(t('statistics.view.table', 'Table view'))}
        size={size}
        {...tooltipProps('table')}
      >
        <Tooltip
          title={t('statistics.view.table', 'Table view')}
          open={openTooltip === 'table'}
          disableFocusListener
          disableHoverListener
        >
          <TableChartIcon sx={{ fontSize: 18 }} />
        </Tooltip>
      </ToggleButton>
      <ToggleButton
        value="download"
        aria-label={String(t('statistics.download', 'Download'))}
        size={size}
        {...tooltipProps('download')}
      >
        <Tooltip
          title={t('statistics.download', 'Download')}
          open={openTooltip === 'download'}
          disableFocusListener
          disableHoverListener
        >
          <DownloadIcon sx={{ fontSize: 18 }} />
        </Tooltip>
      </ToggleButton>
    </ToggleButtonGroup>
  );
};
